import { adminSettingsUtils } from 'utils/selectOptionsUtils';

import {
  EOfferFormatKey,
  ETimeAbbreviationStyle,
  ETimeBaseUnit
} from '../../StoreSettings.types';

import {
  IAbbreviationDropdownField,
  IAbbreviationDropdownOption,
  IAbbreviationOfferTab,
  IAbbreviationToggleField,
  ITimeAbbreviationStyleOption,
  ITimeBaseUnitOption
} from './AbbreviationsSettingsV2.types';

export const ABBREVIATIONS_V2_TEXTS = {
  HEADER_TITLE: 'Number Format',
  HEADER_DESCRIPTION:
    'Set abbreviation rules for product quantities and time-based durations, categorized by the specific type of offer.',
  QUANTITY_SECTION_TITLE: 'Quantity',
  TIME_SECTION_TITLE: 'Time',
  SHOW_FULL_NUMBER_LABEL: 'Show full number up to X digits',
  MAX_DIGITS_LABEL: 'Max digits',
  BASE_UNIT_LABEL: 'Base unit',
  BASE_UNIT_TOOLTIP:
    'The minimum value (in base unit) above which the conversion applies',
  ABBREVIATION_STYLE_LABEL: 'Abbreviation style Format',
  ABBREVIATION_STYLE_TOOLTIP:
    'Choose how each time unit is labeled for time-type Products',
  LOAD_ERROR: 'Unable to load settings. Please try again.',
  RETRY: 'Retry'
};

export const ABBREVIATION_OFFER_TABS: IAbbreviationOfferTab[] = [
  { key: EOfferFormatKey.PRODUCTS, label: 'Bundles' },
  { key: EOfferFormatKey.ROLLING_OFFER, label: 'Rolling Offers' },
  { key: EOfferFormatKey.TRIGGERED_POPUP, label: 'Triggered Popups' },
  { key: EOfferFormatKey.SPECIAL_OFFER, label: 'Special Offers' },
  { key: EOfferFormatKey.PROGRESS_BAR, label: 'Progress Bars' },
  { key: EOfferFormatKey.BALANCE, label: 'Balance' }
];

export const TIME_BASE_UNIT_OPTIONS: ITimeBaseUnitOption[] = [
  { value: ETimeBaseUnit.DAYS, label: 'Days' },
  { value: ETimeBaseUnit.HOURS, label: 'Hours' },
  { value: ETimeBaseUnit.MINUTES, label: 'Minutes' }
];

export const TIME_ABBREVIATION_STYLE_OPTIONS: ITimeAbbreviationStyleOption[] = [
  { value: ETimeAbbreviationStyle.LETTER, label: 'd / h / m' },
  { value: ETimeAbbreviationStyle.SHORT, label: 'D / Hr / Min' },
  {
    value: ETimeAbbreviationStyle.LONG,
    label: 'Days / Hours / Minutes'
  }
];

export const BASE_UNIT_THRESHOLD_VALUE = 1;

export const DEFAULT_TIME_BASE_UNIT = ETimeBaseUnit.MINUTES;

export const TIME_PRESENTATION_FORMAT = 'partial';

export const TIME_BASE_UNIT_TO_PAYLOAD: Record<ETimeBaseUnit, string> = {
  [ETimeBaseUnit.DAYS]: 'day',
  [ETimeBaseUnit.HOURS]: 'hour',
  [ETimeBaseUnit.MINUTES]: 'minute',
  [ETimeBaseUnit.SECONDS]: 'second'
};

export const TIME_UNIT_VALUES = {
  minute: 1,
  hour: 60,
  day: 1440
} as const;

export const TIME_DESCRIPTOR_BY_STYLE: Record<
  ETimeAbbreviationStyle,
  { minute: string; hour: string; day: string }
> = {
  [ETimeAbbreviationStyle.LETTER]: { minute: 'm', hour: 'h', day: 'd' },
  [ETimeAbbreviationStyle.SHORT]: { minute: 'Min', hour: 'Hr', day: 'D' },
  [ETimeAbbreviationStyle.LONG]: {
    minute: 'Minutes',
    hour: 'Hours',
    day: 'Days'
  }
};

export const ABBREVIATION_DROPDOWN_FIELDS: IAbbreviationDropdownField[] = [
  {
    key: 'milSeparator',
    label: 'Thousands Separator'
  },
  {
    key: 'fractionalSeparator',
    label: 'Decimal Separator'
  }
];

export const ABBREVIATION_TOGGLE_FIELDS: IAbbreviationToggleField[] = [
  { key: 'thousandShorthand', label: 'Thousands (K)' },
  { key: 'millionShorthand', label: 'Millions (M)' },
  { key: 'billionShorthand', label: 'Billions (B)' },
  { key: 'trillionShorthand', label: 'Trillions (T)' },
  { key: 'quadrillionShorthand', label: 'Quadrillions (Q)' },
  { key: 'quintillionShorthand', label: 'Quintillions (Qi)' },
  { key: 'sextillionShorthand', label: 'Sextillion (S)' },
  { key: 'septillionShorthand', label: 'Septillion (SP)' }
];

const toDropdownOptions = (
  options: { content: string; value: string }[]
): IAbbreviationDropdownOption[] =>
  options.map((option) => ({ value: option.value, label: option.content }));

export const ABBREVIATION_SEPARATOR_OPTIONS: IAbbreviationDropdownOption[] =
  toDropdownOptions(adminSettingsUtils.separatorOptions);

export const MIN_DIGITS_WITHOUT_SHORTHAND = 3;

export const ABBREVIATION_SWITCH_CLASSNAME =
  'bg-base-input [&>span]:bg-base-background data-[state=checked]:!bg-indigo-600';

export const ABBREVIATION_RADIO_CLASSNAME =
  'border-base-border text-indigo-600 shadow-none';

export const ABBREVIATIONS_V2_TEST_IDS = {
  root: 'abbreviations-v2',
  error: 'abbreviations-v2-error',
  retry: 'abbreviations-v2-retry',
  showFullNumber: 'abbreviations-v2-show-full-number',
  maxDigitsInput: 'abbreviations-v2-max-digits',
  baseUnitTile: 'abbreviations-v2-base-unit-tile',
  abbreviationStyleTile: 'abbreviations-v2-abbreviation-style-tile'
} as const;

export const getAbbreviationTabTestId = (key: EOfferFormatKey): string =>
  `abbreviations-v2-tab-${key}`;

export const getAbbreviationToggleTestId = (key: string): string =>
  `abbreviations-v2-toggle-${key}`;

export const getAbbreviationDropdownTestId = (key: string): string =>
  `abbreviations-v2-dropdown-${key}`;

export const getTimeBaseUnitTestId = (value: string): string =>
  `abbreviations-v2-base-unit-${value}`;

export const getTimeAbbreviationStyleTestId = (value: string): string =>
  `abbreviations-v2-abbreviation-style-${value}`;
